import { Dialog, DialogActions, DialogContent, DialogTitle, Tooltip } from '@mui/material';
import cx from 'classnames';
import AnalyticsUtils from 'js/lib/AnalyticsUtils';
import FilterDSLParser from 'js/lib/FilterDSLParser';
import { completeTxBuilderRecipeGroups } from 'js/lib/RecipeBuilderUtils';
import { find, map } from 'lodash';
import flowTypes from 'new/components/flows/types/flowTypes';
import { ReactComponent as NexsetIcon } from 'new/components/icons/nexsetsOutline.svg';
import { MODE_EXPRESSIONS } from 'new/components/SplitDataSet/DataSetSplitDialog';
import { ROUTE_CONNECT } from 'new/constants/pathnames';
import { flowDataMap } from 'new/elements/flows/dataMap';
import { Stack } from 'new/elements/Stack';
import Typography from 'new/elements/Typography';
import React, { createContext } from 'react';
import { Redirect } from 'react-router-dom';
import { ConnectionLoader } from 'new/elements/ConnectionLoader/ConnectionLoader';
import RoutePrefixContext from '../../../new/contexts/RoutePrefixContext';
import { getFeatureFlagValue } from 'new/constants/featureFlags';
import { FORM_TYPE_CREATE_DATASET } from '../../constants/formTypes';
import {
  ROUTE_DATA_SETS_SHARED_WITH_ME,
  ROUTE_MY_DATA_SETS,
  ROUTE_PIPELINES,
} from '../../constants/pathnames';
import {
  RECIPE_GROUP_TRANSFORMS,
  RECIPE_GROUP_TRANSFORMS_AGGREGATE,
  RECIPE_GROUP_TRANSFORMS_CODE,
} from '../../constants/schemaEditor';
import { SUPPORT_KEY_DATASET_FORM_TX } from '../../constants/supportPageKeys';
import { ACLQueryModeTypes } from '../../constants/visibilityFilters';
import TransformsUtils from '../../lib/TransformsUtils';
import { formatNUIURL, isNUIURLEquals } from '../../lib/URLUtils';
import Button from '../elements/editors/mui/Button';
import Form from '../elements/forms/Form';
import FormPage from '../elements/forms/FormPage';
import RecipeBuilderContainer from '../recipeBuilder/RecipeBuilderContainer';
import { useIsOrgFeatureEnabled } from 'new/hooks/adapters/useIsOrgFeatureEnabled';
import './SendDataForm.scss';

const PIPELINE_FORM_PATH_PATTERN = new RegExp(`${ROUTE_PIPELINES}/\\d+/edit`);
const PIPELINE_PROJECTS_PATH_PATTERN = new RegExp(`(.*)/projects(.*)`);

export const SendDataFormContext = createContext({
  flowType: flowTypes.streaming,
});

class SendDataForm extends React.Component {
  static contextType = RoutePrefixContext;

  state = {
    dataSetName: '',
    dialogError: null,
    redirectPath: null,
    awaitingApplyRecipe: false,
    awaitingDatasetCreation: false,
  };

  componentDidMount() {
    const {
      isEditMode,
      toDataSet,
      fromDataSetId,
      toDataSetId,
      loadRecipeBuilderResources,
      setContextualSupport,
    } = this.props;

    loadRecipeBuilderResources(isEditMode ? toDataSetId : fromDataSetId, isEditMode);

    if (toDataSet) {
      this._setNameFromDataSet();
    } else if (!isEditMode && fromDataSetId) {
      this.setState({
        dataSetName: `Nexset from #${fromDataSetId}`,
      });
    }

    this._loadReusableTransforms();

    if (setContextualSupport) {
      setContextualSupport(SUPPORT_KEY_DATASET_FORM_TX);
    }
  }

  componentWillUnmount() {
    if (this.props.unsetContextualSupport) {
      this.props.unsetContextualSupport(SUPPORT_KEY_DATASET_FORM_TX);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { addedDataset, toDataSet, outputSamplesUpdatedAt } = this.props;
    const { awaitingApplyRecipe, awaitingDatasetCreation } = this.state;

    if (
      (awaitingDatasetCreation && addedDataset && addedDataset !== prevProps.addedDataset) ||
      (awaitingApplyRecipe && outputSamplesUpdatedAt !== prevProps.outputSamplesUpdatedAt)
    ) {
      this._boundValidateOutput();
    }

    if (toDataSet && !prevProps.toDataSet) {
      this._setNameFromDataSet();
    }
  }

  render() {
    const { isModal } = this.props;
    const { redirectPath } = this.state;

    if (redirectPath) {
      return <Redirect to={redirectPath} />;
    }

    const isLoadingInitialData =
      !this.props.vendorsLoaded ||
      !this.props.transformsLoaded ||
      !this.props.fromDataSet ||
      !this.props.schemaEditorInitialized;

    const containerClassName = cx({
      'send-data-form': true,
      'resource-edit-modal': isModal,
    });

    return (
      <SendDataFormContext.Provider value={{ flowType: this.props.flowType }}>
        <div className={containerClassName}>
          <ConnectionLoader loading={isLoadingInitialData} message="Loading Nexset..." />

          <Form
            isModal={isModal}
            alwaysShowCancel
            submitButtonText="Done"
            title={this.getFormTitle()}
            type={FORM_TYPE_CREATE_DATASET}
            onSubmit={this._handleFormSubmitted}
            onDismiss={this._handleFormDismissed}
          >
            <FormPage
              title="Set Rules on Data"
              nextButtons={[
                {
                  variant: 'contained',
                  label: 'Save & Close',
                  buttonTheme: 'primary',
                  validationMethod: this._validateOutput,
                },
                {
                  label: 'Save',
                  onClick: this._handleSaveAndStay,
                },
              ]}
            >
              <RecipeBuilderContainer
                id={0}
                type={FORM_TYPE_CREATE_DATASET}
                dataSet={this.props.fromDataSet}
                dataSetName={this.state.dataSetName}
                toDataSet={this.props.toDataSet}
                inputFlatSchema={this.props.fromDataSetSchema}
                outputFlatSchema={this.props.toDataSetSchema}
                isLoadingInitialData={isLoadingInitialData}
                isEditMode={this.props.isEditMode}
                isSplitMode={this.props.isSplitMode}
                loadReusableTransforms={this._loadReusableTransforms}
                onDataSetNameChanged={this._handleDataSetNameChanged}
              />
            </FormPage>
          </Form>

          <Dialog
            fullWidth
            maxWidth="sm"
            open={!!this.state.dialogError}
            onClose={this._onCloseErrorDialogClicked}
          >
            <DialogTitle disableTypography={true}>
              <h3
                style={{
                  fontSize: 17,
                  fontWeight: 'var(--font-weight-regular)',
                  color: 'rgba(0, 0, 0, 0.87)',
                }}
              >
                Error
              </h3>
            </DialogTitle>
            <DialogContent>
              <div
                style={{
                  fontSize: 'var(--font-size-base)',
                  color: 'rgba(0, 0, 0, 0.6)',
                }}
              >
                {this.state.dialogError}
              </div>
            </DialogContent>
            <DialogActions>
              <Button color="primary" onClick={this._onCloseErrorDialogClicked}>
                OK
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      </SendDataFormContext.Provider>
    );
  }

  getFormTitle = () => {
    const subtitle = this._generateSubtitle(this.props.currentStage);

    //get flow type title from flowType prop using flowDataMap. add checks so that it doesn't break if flowType is not present in flowDataMap
    const flowTypeTitle = flowDataMap[this.props.flowType]
      ? flowDataMap[this.props.flowType].shortTitle
      : null;

    const label = this.props.isEditMode ? 'Editing Nexset' : 'New Nexset from';

    return (
      <Stack direction="row" alignItems="center" gap="16px">
        <Typography variant="bodyLarge">
          {flowTypeTitle}
          {this.props.flowType !== flowTypes.api_server && (
            <>
              {flowTypeTitle ? (
                <span style={{ color: 'var(--color-greyscale-200)' }}> / </span>
              ) : (
                ''
              )}
              {label}
            </>
          )}
        </Typography>
        <div className="current-nexset-box">
          <NexsetIcon width={16} height={16} />
          <Tooltip title={subtitle || ''} placement="top">
            <Typography variant="bodyLarge">{subtitle}</Typography>
          </Tooltip>
        </div>
      </Stack>
    );
  };

  _setNameFromDataSet = () => {
    const { toDataSet } = this.props;

    this.setState({ dataSetName: toDataSet.name });
  };

  _loadReusableTransforms = () => {
    if (!this.props.isLoadingReusableTransforms) {
      this.props.getAllReusableTransforms(ACLQueryModeTypes.MODE_COLLABORATOR.key);
    }
  };

  _handleDataSetNameChanged = (name) => {
    this.setState({ dataSetName: name });
  };

  _handleFormDismissed = () => {
    const { previousPath, destinationDraft, isModal, onDismiss } = this.props;

    const { pathname } = this.props.location || {};

    if (isModal && onDismiss) {
      onDismiss?.(this.props.formData);
      return;
    }

    if (Boolean(destinationDraft)) {
      this._redirectAfterCompletion(`${ROUTE_CONNECT}${ROUTE_PIPELINES}`);

      return;
    }

    this._redirectAfterCompletion(
      previousPath ||
        (pathname.startsWith(ROUTE_DATA_SETS_SHARED_WITH_ME)
          ? ROUTE_DATA_SETS_SHARED_WITH_ME
          : ROUTE_MY_DATA_SETS),
    );
  };

  _handleFormSubmitted = () => {
    this._redirectAfterCompletion(ROUTE_MY_DATA_SETS);
  };

  _handleEnterReviewStage = () => {
    this.props.markFormCompleted(FORM_TYPE_CREATE_DATASET);
  };

  _handleSaveAndStay = () => {
    const { isEditMode, formData, setFormError, setFormAwaitingValidation, isModal } = this.props;

    setFormAwaitingValidation(FORM_TYPE_CREATE_DATASET, true);
    setFormError(FORM_TYPE_CREATE_DATASET, null);

    this._validateOutput(
      formData,
      () => {
        setFormAwaitingValidation(FORM_TYPE_CREATE_DATASET, false);

        if (!isEditMode) {
          const {
            history,
            formData: { addedDatasetId },
          } = this.props;

          /**
           * preveting URL change if the form is opened in a modal
           */
          if (!isModal) {
            const url = `${this.context}${ROUTE_MY_DATA_SETS}/${addedDatasetId}/edit`;
            history.replace(url);
          }
        }
      },
      (error) => {
        setFormAwaitingValidation(FORM_TYPE_CREATE_DATASET, false);
        setFormError(FORM_TYPE_CREATE_DATASET, error);
      },
    );
  };

  _redirectAfterCompletion = (listingPath) => {
    const { fromDataSetId, fromDataSet, currentPath, previousPath, isModal, onDismiss } =
      this.props;

    if (isModal && onDismiss) {
      this.props.onDismiss?.(this.props.formData);
      return;
    }

    const pipelineFormPath = this._getPipelineFormPath();
    const didRouteChange =
      new RegExp(`${ROUTE_MY_DATA_SETS}/\\d+/new`).test(previousPath) &&
      new RegExp(`${ROUTE_MY_DATA_SETS}/\\d+/edit`).test(currentPath);
    let redirectPath;

    if (pipelineFormPath) {
      redirectPath = pipelineFormPath;
    } else if (
      isNUIURLEquals(this.context, previousPath, ROUTE_PIPELINES) ||
      PIPELINE_PROJECTS_PATH_PATTERN.test(previousPath) ||
      // if the user first presses Save and then Save & Close
      didRouteChange
    ) {
      // navigate to pipelines
      if (didRouteChange) {
        redirectPath = `${this.context}${ROUTE_PIPELINES}`;
      } else {
        redirectPath = `${previousPath}`;
      }
    } else {
      redirectPath = listingPath || formatNUIURL(this.context, listingPath);
    }

    this.setState({ redirectPath });
  };

  _getPipelineFormPath = () => {
    const { previousPath } = this.props;

    if (previousPath && PIPELINE_FORM_PATH_PATTERN.test(previousPath)) {
      return previousPath;
    }

    return null;
  };

  _requestDatasetCreation = (newDatasetInfo, parentDataSetId, outputValidations) => {
    // in legacy mode, regenerate the code from each recipe
    let recipeGroups = this.props.recipeGroups;

    if (this.props.isLegacyMode) {
      recipeGroups = map(recipeGroups, (recipeGroup) =>
        recipeGroup.type === RECIPE_GROUP_TRANSFORMS
          ? {
              ...recipeGroup,
              data: map(recipeGroup.data, (recipe) => ({
                ...recipe,
                code: undefined,
              })),
            }
          : recipeGroup,
      );

      AnalyticsUtils.captureCustomEvent('nexset_designer_legacy_mode_save');
    } else {
      AnalyticsUtils.captureCustomEvent('nexset_designer_dsl_mode_save');
    }

    const txSpec = {
      v: 2,
      spec: completeTxBuilderRecipeGroups(recipeGroups, this.props.transforms),
    };

    // save
    if (this.props.isEditMode) {
      this.props.updateDataSetRecipe(
        this.props.toDataSetId,
        parentDataSetId,
        newDatasetInfo.annotations,
        newDatasetInfo.transform,
        newDatasetInfo.has_custom_transform,
        newDatasetInfo.transform_id,
        txSpec,
        outputValidations,
        this.props.outputSchema,
        this.props.rawOutputSchema,
        this.props.rawOutputSamples,
        this.state.dataSetName,
      );
    } else {
      this.props.addDatasetFromForm(
        FORM_TYPE_CREATE_DATASET,
        newDatasetInfo,
        parentDataSetId,
        txSpec,
        outputValidations,
        this.props.outputSchema,
        this.props.rawOutputSchema,
        this.props.rawOutputSamples,
      );
    }
  };

  _generateSubtitle = (stageId) => {
    const { isEditMode, fromDataSet, toDataSet } = this.props;
    const dataSet = isEditMode ? toDataSet : fromDataSet;
    let resourceId = dataSet?.id;
    let resourceName = dataSet?.name;
    let subtitle = null;

    if (stageId === 0) {
      subtitle = isEditMode ? '' : 'Nexset: ';
      subtitle = `${subtitle}${resourceName} (#${resourceId})`;
    }

    return subtitle;
  };

  _validateOutput = (data, onSuccess, onError) => {
    const {
      transforms,
      fromDataSet,
      fromDataSetId,
      toDataSet,
      inputSamples,
      outputSchema,
      recipeGroups,
      splitter,
      isLegacyMode,
      isRecipeUnapplied,
      isSplitMode,
      isSqlTransformEnabled,
      applyRecipe,
      splitDataSet,
      markFormCompleted,
      updateFormData,
    } = this.props;
    const { dataSetName, awaitingApplyRecipe, awaitingDatasetCreation } = this.state;

    this._boundValidateOutput = this._validateOutput.bind(this, data, onSuccess, onError);

    /**
     * @Guard
     * - Validate SQL Transform feature flag
     * - Prevent saving aggregate transforms when feature is NOT explicitly enabled
     * - Block for: false, null, undefined (only allow when true)
     */
    const hasAggregateTransform = find(
      recipeGroups,
      ({ type }) => type === RECIPE_GROUP_TRANSFORMS_AGGREGATE,
    );

    // TODO: Temporarily disabling this guard to allow aggregate transforms to be saved when feature is not explicitly enabled. Ideally, we should only allow aggregate transforms to be saved when feature is explicitly enabled.
    // if (hasAggregateTransform && isSqlTransformEnabled !== true) {
    //   onError(
    //     'SQL Transform feature is not enabled for your organization. ' +
    //       'Please remove the aggregate transform or contact support.'
    //   );
    //   return;
    // }

    // if Nexset creation was successful, validation complete
    if (awaitingDatasetCreation) {
      this.setState({
        awaitingDatasetCreation: false,
      });

      onSuccess();
      return;
    }

    if (awaitingApplyRecipe || !isRecipeUnapplied) {
      this.setState({
        awaitingApplyRecipe: false,
      });

      // create Nexset
      const outputConfig = {
        name: dataSetName || `Nexset from ${fromDataSetId}`,
      };
      const codeRecipeGroup = find(
        recipeGroups,
        (type) =>
          type === RECIPE_GROUP_TRANSFORMS_CODE || type === RECIPE_GROUP_TRANSFORMS_AGGREGATE,
      );
      // Get feature flag value for multi-block transforms
      const enableMultiBlock = getFeatureFlagValue('UI_ENABLE_MULTI_BLOCK_TRANSFORMS');

      const { txObject, saObject, reusableTransformId, outputValidations } =
        TransformsUtils.getTxFromRecipe(recipeGroups, transforms, isLegacyMode, enableMultiBlock);

      if (txObject) {
        outputConfig.transform = txObject;
        outputConfig.has_custom_transform = !!codeRecipeGroup;
      }

      if (reusableTransformId) {
        outputConfig.transform_id = reusableTransformId;
      }

      if (saObject) {
        outputConfig.annotations = saObject;
      }

      if (isSplitMode) {
        const { mode, expressions, attr, values, fallback } = splitter;
        let parsingError = null;

        if (mode === MODE_EXPRESSIONS) {
          for (const expression of expressions) {
            try {
              FilterDSLParser.parse(expression.code);
            } catch (error) {
              parsingError = error.message;
              break;
            }
          }
        }

        if (parsingError) {
          onError(parsingError);
          return;
        }

        splitDataSet(fromDataSet?.id, toDataSet?.id, mode, expressions, attr, values, fallback)
          .then((response) => {
            const { result: addedDatasetId } = response;

            markFormCompleted(FORM_TYPE_CREATE_DATASET);
            updateFormData(FORM_TYPE_CREATE_DATASET, { addedDatasetId });

            // this is a hack to wait for the form to update before calling
            // onSuccess
            setTimeout(() => {
              onSuccess();
            }, 1);
          })
          .catch((error) => {
            console.log('splitDataSet error', error);
            onError(String(error));
          });
      } else {
        this.setState(
          {
            awaitingDatasetCreation: true,
          },
          () => {
            this._requestDatasetCreation(outputConfig, fromDataSet?.id, outputValidations);
          },
        );
      }
      return;
    }

    // apply recipe to check that the output schema is not empty
    if (isRecipeUnapplied) {
      this.setState(
        {
          awaitingApplyRecipe: true,
        },
        () => {
          applyRecipe(recipeGroups, inputSamples);
        },
      );
    }
  };

  _onCloseErrorDialogClicked = () => {
    this.setState({ dialogError: null });
  };
}

/**
 * Helper component to inject SQL Transform feature flag into SendDataForm
 */
const SendDataFormWithFeatureCheck = (props) => {
  const isSqlTransformEnabled = useIsOrgFeatureEnabled('sql_transform');

  return <SendDataForm {...props} isSqlTransformEnabled={isSqlTransformEnabled} />;
};

export default SendDataFormWithFeatureCheck;
